/** @jsxImportSource @emotion/react */
import { FC } from 'react'
import { Form as FinalForm } from 'react-final-form'
import { Button } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { PlatformSlug } from 'constants/consts'

import { RewardTypeEnum } from '__generatedTypes__/globalTypes'
import { validateRequired } from 'utils/validation'
import { setFieldData } from 'utils/setFieldData'
import { SelectField } from 'components/Select'
import { PriceInput } from 'components/Input/PriceInput'
import TextareaAutosize from 'components/TextareaAutosize/TextareaAutosize'

import { getExtensionOptions, hasRewardImage } from '../consts'
import { AchievementsRewardsImagePreview } from '../AchievementsRewardsImagePreview'
import * as achievementsClasses from '../styles'
import * as classes from './styles'
import { getRewardTypeOptions } from './consts'
import { validateMysteryBoxPercentage } from './utils'

export const READ_ONLY_FOR_PLATFORMS: PlatformSlug[] = [
  PlatformSlug.MCATBooster,
  PlatformSlug.GREBooster,
  PlatformSlug.NBEOPartOne,
]

export const DARK_MODE_ONLY_PLATFORMS: PlatformSlug[] = [PlatformSlug.CARSBooster]

export type FormValues = {
  type: {
    id: RewardTypeEnum
    title: string
  }
  mysteryBoxPercentage: number
  description: string
  extension?: { id: string; title: string }
  additionalTest?: { id: string; title: string; rewardIdx: number }
}

type Props = {
  initialValues?: FormValues
  onSubmit: (values: FormValues) => Promise<void>
  platform: PlatformSlug
}

export const Form: FC<Props> = ({ initialValues, onSubmit, platform }) => {
  const navigate = useNavigate()
  const isReadOnly = READ_ONLY_FOR_PLATFORMS.includes(platform)

  return (
    <FinalForm<FormValues>
      mutators={{ setFieldData }}
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ submitting, handleSubmit, values }) => {
        const rewardType = values.type?.id
        const rewardSubType = values.extension?.id
        const shouldRenderImagePreview = !!rewardType && hasRewardImage(rewardType, rewardSubType)

        return (
          <form data-testid="reward-form" css={classes.form} onSubmit={handleSubmit}>
            <SelectField
              data-testid="reward-type"
              name="type"
              validate={validateRequired}
              items={getRewardTypeOptions(platform)}
              label="Reward Type"
              placeholder="Choose Reward Type"
              disabled={isReadOnly || !!initialValues?.type.id}
              fullWidth
            />

            {values.type?.id === RewardTypeEnum.Extension && (
              <SelectField
                data-testid="extension"
                name="extension"
                validate={validateRequired}
                items={getExtensionOptions(platform)}
                label="Reward details"
                placeholder="Choose Extension"
                disabled={isReadOnly}
                fullWidth
              />
            )}

            <TextareaAutosize
              data-testid="description"
              name="description"
              label="Description"
              placeholder="Enter Description"
              validate={validateRequired}
              css={classes.textarea}
              disabled={isReadOnly}
            />

            <PriceInput
              data-testid="mystery-box-percentage"
              name="mysteryBoxPercentage"
              validate={validateMysteryBoxPercentage}
              label="Percentage to obtain from Mystery Box"
              placeholder="Enter percentage"
              disabled={isReadOnly}
              fullWidth
            />

            {shouldRenderImagePreview && (
              <div css={classes.columnsWrapper}>
                {!DARK_MODE_ONLY_PLATFORMS.includes(platform) && (
                  <div>
                    <div css={classes.labelWrapper}>Light Version</div>
                    <div css={achievementsClasses.imagePreviewWrapper}>
                      <AchievementsRewardsImagePreview
                        data-testid="image-light"
                        tab="rewards"
                        rewardType={rewardType}
                        subType={rewardSubType}
                        mode="light"
                      />
                    </div>
                  </div>
                )}
                <div>
                  <div css={classes.labelWrapper}>Dark Version</div>
                  <div css={achievementsClasses.imagePreviewWrapper}>
                    <AchievementsRewardsImagePreview
                      data-testid="image-dark"
                      tab="rewards"
                      rewardType={rewardType}
                      subType={rewardSubType}
                      mode="dark"
                    />
                  </div>
                </div>
              </div>
            )}

            <div css={classes.formActions}>
              <Button
                data-testid="cancel-button"
                variant="outlined"
                color="red"
                size="large"
                onClick={() => navigate(-1)}
                disabled={submitting}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                data-testid="submit-button"
                color="blue"
                type="submit"
                size="large"
                disabled={isReadOnly || submitting}
                fullWidth
              >
                Save Edits
              </Button>
            </div>
          </form>
        )
      }}
    />
  )
}
